import React, { useState, useEffect } from 'react';
import { User } from '../types';
import './UserSettingsModal.css';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUserUpdate: (updatedUser: Partial<User>) => void;
}

interface AppearanceSettings {
  theme: 'light' | 'dark' | 'auto';
  reduceMotion: boolean;
  avatarColor: string;
}

const defaultSettings: AppearanceSettings = {
  theme: 'auto',
  reduceMotion: false,
  avatarColor: '#00b194'
};

const UserSettingsModal = React.memo<UserSettingsModalProps>(({
  isOpen,
  onClose,
  user,
  onUserUpdate
}) => {
  const [settings, setSettings] = useState<AppearanceSettings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('unichat-appearance-settings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings({ ...defaultSettings, ...parsed });
    }
  }, []);

  // Apply settings to document when they change
  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  const applySettings = (newSettings: AppearanceSettings) => {
    const root = document.documentElement;
    
    // Apply theme
    if (newSettings.theme === 'auto') {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      root.setAttribute('data-theme', newSettings.theme);
    }
    
    // Font size removed - was not working properly
    
    // Apply reduced motion
    root.setAttribute('data-reduce-motion', newSettings.reduceMotion.toString());
    
    // Apply avatar color
    root.style.setProperty('--user-avatar-color', newSettings.avatarColor);
  };

  const updateSetting = <K extends keyof AppearanceSettings>(
    key: K, 
    value: AppearanceSettings[K]
  ) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setHasChanges(true);
  };

  const saveSettings = () => {
    localStorage.setItem('unichat-appearance-settings', JSON.stringify(settings));
    applySettings(settings);
    setHasChanges(false);
    
    // Show brief confirmation
    const root = document.documentElement;
    root.style.setProperty('--save-confirmation', 'Settings saved!');
    setTimeout(() => {
      root.style.removeProperty('--save-confirmation');
    }, 2000);
  };

  const resetToDefaults = () => {
    setSettings(defaultSettings);
    setHasChanges(true);
  };

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (settings.theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        applySettings(settings);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [settings]);

  if (!isOpen) return null;

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal appearance-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-header">
          <div className="header-info">
            <h2 className="settings-title">🎨 Appearance Settings</h2>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="settings-content">
          <div className="settings-main appearance-settings">
            
            {/* Theme Selection */}
            <div className="setting-group">
              <label className="setting-label">
                <span className="label-text">🌗 Theme</span>
                <span className="label-description">Choose your preferred color scheme</span>
              </label>
              <div className="theme-options">
                {([
                  { value: 'light', label: 'Light', icon: '☀️', description: 'Bright and clean' },
                  { value: 'dark', label: 'Dark', icon: '🌙', description: 'Easy on the eyes' },
                  { value: 'auto', label: 'Auto', icon: '⚙️', description: 'Matches your system' }
                ] as const).map(theme => (
                  <button
                    key={theme.value}
                    className={`theme-option ${settings.theme === theme.value ? 'active' : ''}`}
                    onClick={() => updateSetting('theme', theme.value)}
                  >
                    <div className="theme-preview" data-theme={theme.value}>
                      <div className="preview-bg">
                        <div className="preview-text"></div>
                        <div className="preview-accent"></div>
                      </div>
                    </div>
                    <div className="theme-info">
                      <div className="theme-name">
                        <span className="theme-icon">{theme.icon}</span>
                        {theme.label}
                      </div>
                      <div className="theme-description">{theme.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size removed - was not working properly */}

            {/* Accessibility Options */}
            <div className="setting-group">
              <label className="setting-label">
                <span className="label-text">♿ Accessibility</span>
                <span className="label-description">Options to improve usability</span>
              </label>
              
              <div className="toggle-options">
                <div className="toggle-item">
                  <div className="toggle-info">
                    <div className="toggle-label">🎬 Reduce Motion</div>
                    <div className="toggle-description">Minimize animations and transitions</div>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={settings.reduceMotion}
                      onChange={(e) => updateSetting('reduceMotion', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Avatar Color Customization */}
            <div className="setting-group">
              <label className="setting-label">
                <span className="label-text">👤 Avatar Color</span>
                <span className="label-description">Customize your avatar color</span>
              </label>
              
                              <div className="color-options">
                  {[
                    { value: '#00b194', label: 'Teal', name: 'UniFirst Teal' },
                    { value: '#e91e63', label: 'Pink', name: 'Pink' },
                    { value: '#2196f3', label: 'Blue', name: 'Blue' },
                    { value: '#4caf50', label: 'Green', name: 'Green' },
                    { value: '#ff9800', label: 'Orange', name: 'Orange' },
                    { value: '#9c27b0', label: 'Purple', name: 'Purple' },
                    { value: '#f44336', label: 'Red', name: 'Red' },
                    { value: '#795548', label: 'Brown', name: 'Brown' }
                  ].map(color => (
                  <button
                    key={color.value}
                    className={`color-option ${settings.avatarColor === color.value ? 'active' : ''}`}
                    onClick={() => updateSetting('avatarColor', color.value)}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {settings.avatarColor === color.value && (
                      <span className="color-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
              

            </div>

            {/* Preview section removed per user request */}

          </div>
        </div>

        <div className="settings-footer">
          <div className="footer-left">
            <button className="btn secondary" onClick={resetToDefaults}>
              Reset to Defaults
            </button>
          </div>
          <div className="footer-right">
            <button className="btn secondary" onClick={onClose}>Close</button>
            <button 
              className={`btn primary ${hasChanges ? '' : 'disabled'}`}
              onClick={saveSettings}
              disabled={!hasChanges}
            >
              {hasChanges ? 'Save Changes' : 'No Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default UserSettingsModal; 